import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { leadApi, activityApi, projectApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calendar,
  Pencil,
  Trash2,
  Plus,
  FileText,
  MessageSquare,
  FolderPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import LeadFiles from '../components/LeadFiles';
import EstimateSection from '../components/EstimateSection';

const SALES_STAGES = ['Lead', 'Qualified', 'Estimate', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const ACTIVITY_TYPES = ['note', 'call', 'email', 'meeting', 'follow_up'];

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newActivity, setNewActivity] = useState({ type: 'note', description: '' });
  const [addingActivity, setAddingActivity] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [leadRes, activitiesRes] = await Promise.all([
        leadApi.getOne(id),
        activityApi.getAll({ lead_id: id }),
      ]);
      setLead(leadRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await leadApi.updateStage(id, newStage);
      setLead({ ...lead, stage: newStage });
      toast.success(`Lead moved to ${newStage}`);
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadApi.delete(id);
      toast.success('Lead deleted');
      navigate('/leads');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!newActivity.description.trim()) return;
    setAddingActivity(true);
    try {
      const response = await activityApi.create({
        lead_id: id,
        type: newActivity.type,
        description: newActivity.description,
      });
      setActivities([response.data, ...activities]);
      setNewActivity({ type: 'note', description: '' });
      toast.success('Activity added');
    } catch (error) {
      toast.error('Failed to add activity');
    } finally {
      setAddingActivity(false);
    }
  };

  const handleConvertToProject = async () => {
    navigate(`/projects/new?lead_id=${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/leads')}
            className="rounded-sm"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {lead.first_name && lead.last_name 
                ? `${lead.first_name} ${lead.last_name}` 
                : lead.name}
            </h1>
            {lead.company && (
              <p className="text-sm font-medium text-muted-foreground">{lead.company}</p>
            )}
            <p className="text-muted-foreground">{lead.project_type || 'No project type'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {lead.stage === 'Won' && (
            <Button
              onClick={handleConvertToProject}
              className="rounded-sm bg-green-700 hover:bg-green-800 btn-press text-sm"
              data-testid="convert-to-project-btn"
            >
              <FolderPlus className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Convert to Project</span>
              <span className="sm:hidden">Convert</span>
            </Button>
          )}
          {lead.stage !== 'Lost' && (
            <Link to={`/leads/${id}/edit`}>
              <Button variant="outline" className="rounded-sm text-sm" data-testid="edit-lead-btn">
                <Pencil className="h-4 w-4 mr-1 sm:mr-2" />
                Edit
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            className="rounded-sm text-red-600 hover:text-red-700"
            onClick={handleDelete}
            data-testid="delete-lead-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stage Selector */}
          <Card className="rounded-sm border-t-4 border-t-orange-500">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="text-sm font-medium">Stage:</span>
                <Select value={lead.stage} onValueChange={handleStageChange}>
                  <SelectTrigger className="w-[200px] rounded-sm" data-testid="stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SALES_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {stage}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge className={`status-${lead.stage.toLowerCase()} ml-auto`}>
                  {lead.stage}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {lead.email && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-secondary rounded-sm flex items-center justify-center">
                    <Mail className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <a href={`mailto:${lead.email}`} className="text-sm hover:text-orange-500">
                      {lead.email}
                    </a>
                  </div>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-secondary rounded-sm flex items-center justify-center">
                    <Phone className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <a href={`tel:${lead.phone}`} className="text-sm hover:text-orange-500">
                      {lead.phone}
                    </a>
                  </div>
                </div>
              )}
              {(lead.address || lead.city) && (
                <div className="flex items-center gap-3 sm:col-span-2">
                  <div className="h-10 w-10 bg-secondary rounded-sm flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Address</p>
                    <p className="text-sm">
                      {lead.address && <span>{lead.address}, </span>}
                      {lead.city && <span>{lead.city}, </span>}
                      {lead.state} {lead.zip_code}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Activity Form */}
              <form onSubmit={handleAddActivity} className="mb-6">
                <div className="flex gap-2 mb-2">
                  <Select
                    value={newActivity.type}
                    onValueChange={(val) => setNewActivity({ ...newActivity, type: val })}
                  >
                    <SelectTrigger className="w-[140px] rounded-sm" data-testid="activity-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Add a note, log a call, or record activity..."
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                  className="rounded-sm mb-2"
                  rows={3}
                  data-testid="activity-description"
                />
                <Button
                  type="submit"
                  disabled={addingActivity || !newActivity.description.trim()}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                  data-testid="add-activity-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Activity
                </Button>
              </form>

              {/* Activities List */}
              {activities.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No activities yet</p>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity) => (
                    <div key={activity.id} className="flex gap-4 pb-4 border-b last:border-0">
                      <div className="h-8 w-8 bg-secondary rounded-sm flex items-center justify-center flex-shrink-0">
                        {activity.type === 'note' ? (
                          <FileText className="h-4 w-4" strokeWidth={1.5} />
                        ) : activity.type === 'call' ? (
                          <Phone className="h-4 w-4" strokeWidth={1.5} />
                        ) : activity.type === 'email' ? (
                          <Mail className="h-4 w-4" strokeWidth={1.5} />
                        ) : (
                          <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {activity.type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(activity.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        <p className="text-sm">{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Files & Documents */}
          <LeadFiles leadId={id} />

          {/* Estimates */}
          <EstimateSection leadId={id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Lead Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estimated Value</span>
                <span className="font-semibold text-green-700">
                  {formatCurrency(lead.estimated_value)}
                </span>
              </div>
              {lead.estimate_total > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Estimate Total</span>
                  <span className="font-semibold text-orange-600">
                    {formatCurrency(lead.estimate_total)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Source</span>
                <span className="text-sm">{lead.source || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="text-sm">
                  {new Date(lead.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Updated</span>
                <span className="text-sm">
                  {new Date(lead.updated_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {lead.notes && (
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
